/**
 * Composable for design import/export (serialization).
 *
 * Handles:
 * - Exporting the current design state to a serializable format
 * - Importing design data from PHP/JSON into stores
 * - Converting between legacy serialized format and Pinia stores
 */
import { useDesignStore, type ViewName } from '../stores/designStore';
import { useProductStore } from '../stores/productStore';
import { useTeamStore } from '../stores/teamStore';
import { eventBus } from '@core/EventBus';
import type { DesignItem } from '@core/types';
import { rehydrateSvgItem, resolveSvgSourceUrl } from './useSvgRecolor';
import { useFonts } from './useFonts';
import { useFontEmbedding } from './useFontEmbedding';

export function serializeDesignItems(
  items: Partial<Record<string, DesignItem[]>>,
): Record<string, DesignItem[]> {
  const serialized: Record<string, DesignItem[]> = {};
  for (const [view, viewItems] of Object.entries(items)) {
    serialized[view] = (viewItems ?? []).map((item) => {
      const copy = { ...item };
      if (copy.type === 'svg') {
        const stableSource = resolveSvgSourceUrl(copy);
        if (stableSource) {
          copy.src = stableSource;
          delete copy.svg;
        }
      } else if (Number(copy.upload) === 1 && copy.fileUrl) {
        delete copy.svg;
        if (String(copy.src ?? '').startsWith('blob:')) copy.src = copy.fileUrl;
      }
      return copy;
    });
  }
  return serialized;
}

export function canRehydrateSvgItem(item: DesignItem): boolean {
  return item.type === 'svg' && Boolean(
    resolveSvgSourceUrl(item)
    || String((item as Record<string, unknown>).shapeSourceSvg ?? '').trim(),
  );
}

export function useDesignIO() {
  const design = useDesignStore();
  const product = useProductStore();
  const team = useTeamStore();
  let hydrationPromise: Promise<void> | null = null;

  function hydrateLoadedDesignAssets(): Promise<void> {
    if (hydrationPromise) return hydrationPromise;
    hydrationPromise = (async () => {
      const svgItems = Object.values(design.items)
        .flat()
        .filter(canRehydrateSvgItem);
      const items = Object.values(design.items).flat();
      await Promise.allSettled([
        ...svgItems.map((item) => rehydrateSvgItem(item)),
        useFonts().activateFontsForItems(items),
        useFontEmbedding().prefetchEmbeddableFonts(items),
      ]);
    })().finally(() => {
      hydrationPromise = null;
    });
    return hydrationPromise;
  }

  const hydrateAfterLoad = () => { void hydrateLoadedDesignAssets(); };
  eventBus.on('after.load.design' as string, hydrateAfterLoad);

  /** Export current state to a JSON-serializable object */
  function exportDesign(): Record<string, unknown> {
    return {
      version: 2,
      productId: product.product?.id,
      colorIndex: product.activeColorIndex,
      activeView: design.activeView,
      items: serializeDesignItems(design.items),
      teams: team.isTeamMode
        ? {
            members: team.members,
          }
        : null,
      output: design.output,
    };
  }

  /** Import design data from a loaded JSON blob (e.g., from server) */
  function importDesign(data: Record<string, unknown>) {
    eventBus.emit('before.import.design' as string, data);

    if (data.activeView) {
      const view = data.activeView as ViewName;
      if (design.views.includes(view)) {
        design.switchView(view);
      }
    }

    if (data.items && typeof data.items === 'object') {
      const itemsMap = data.items as Record<string, unknown[]>;
      for (const [viewName, items] of Object.entries(itemsMap)) {
        if (Array.isArray(items)) {
          for (const item of items) {
            if (!item || typeof item !== 'object') continue;
            const rec = item as Record<string, unknown>;
            const idVal = rec.id;
            const id =
              idVal != null &&
              idVal !== '' &&
              idVal !== 0 &&
              String(idVal) !== '0'
                ? String(idVal)
                : design.nextId();
            design.addItem({ ...rec, id } as DesignItem, viewName as ViewName);
          }
        }
      }
    }

    if (data.teams && typeof data.teams === 'object') {
      const teamsData = data.teams as { members?: unknown[] };
      if (Array.isArray(teamsData.members)) {
        team.enableTeamMode();
        for (const m of teamsData.members) {
          if (!m || typeof m !== 'object') continue;
          const r = m as Record<string, unknown>;
          team.addMember({
            name: String(r.name ?? ''),
            number: String(r.number ?? ''),
            size: String(r.size ?? ''),
          });
        }
      }
    }

    if (data.colorIndex !== undefined) {
      product.changeColor(Number(data.colorIndex));
    }

    eventBus.emit('after.import.design' as string, data);
  }

  /** Import from legacy save/load payload (pre–Vue iframe editor). */
  function importLegacy(legacyItems: Record<string, unknown>) {
    // Legacy payloads may reference DOM in older builds; importDesign expects JSON-safe items.
    // this converter handles the basic JSON-serialized subset
    // used by the save/load API
    if (typeof legacyItems === 'object' && legacyItems !== null) {
      importDesign(legacyItems as Record<string, unknown>);
    }
  }

  /** Clear all design data */
  function clearDesign() {
    eventBus.emit('before.clear.design' as string, undefined);
    design.clearAll();
    team.clearAll();
    eventBus.emit('after.clear.design' as string, undefined);
  }

  function dispose() {
    eventBus.off('after.load.design' as string, hydrateAfterLoad);
  }

  return {
    exportDesign,
    importDesign,
    importLegacy,
    clearDesign,
    hydrateLoadedDesignAssets,
    dispose,
  };
}
